/** @jsxImportSource @emotion/react */
import { DashboardPromo, DashboardPromoSkeleton } from 'components';
import { useMerchantData } from 'contexts';
import { FeatureFlag, PageAction } from 'enums';
import { isSandbox } from 'global';
import { useFeatureFlagEnabled } from 'hooks';
import { FC, useEffect, useRef, useState } from 'react';
import SwipeableViews from 'react-swipeable-views';
import { autoPlay } from 'react-swipeable-views-utils';
import { DashboardPromo as ContentfulDashboardPromo } from 'types';
import { logError, useContentful } from 'utils';
import * as styles from './PromoCarousel.styles';

const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

const PromoCarousel: FC = () => {
  const { getContent } = useContentful();
  const [content, setContent] = useState<ContentfulDashboardPromo[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { daysSinceMerchantCreation } = useMerchantData();
  const { shouldShowMfaManagment: isAdminUser } = useMerchantData();
  const selectiveMfaEnabled = useFeatureFlagEnabled(
    FeatureFlag.EnableSelectiveMFA
  );
  const isMountedRef = useRef(true);

  const loadContent = async (): Promise<void> => {
    if (isMountedRef.current) {
      setIsLoading(true);
    }

    try {
      const contentData = (
        await getContent<ContentfulDashboardPromo>('dashboardPromo', {
          'fields.carouselOrder[gte]': '1',
        })
      )
        ?.filter((promo) => {
          // If rangeStart/rangeEnd are specified in Contentful, ensure: rangeStart <= daysSinceMerchantCreation <= rangeEnd
          const merchantCreatedWithinRangeIfSpecified =
            (promo.merchantCreationRangeStartInDays === undefined ||
              daysSinceMerchantCreation >=
                promo.merchantCreationRangeStartInDays) &&
            (promo.merchantCreationRangeEndInDays === undefined ||
              daysSinceMerchantCreation <=
                promo.merchantCreationRangeEndInDays);

          const isMfaRelated = promo.id === 'mfa-promotion-banner';

          if (isMfaRelated) {
            return (
              isAdminUser() &&
              selectiveMfaEnabled &&
              merchantCreatedWithinRangeIfSpecified
            );
          }

          return merchantCreatedWithinRangeIfSpecified;
        })
        ?.sort((a, b) => a.carouselOrder - b.carouselOrder);

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setContent(contentData);
      }
    } catch (err) {
      logError(PageAction.fetchContentfulContent, err, {
        contentTypeId: 'dashboardPromo',
      });
    } finally {
      // Only update loading state if component is still mounted
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!isSandbox) {
      loadContent();
    }

    return (): void => {
      isMountedRef.current = false;
    };
  }, []);

  const handleStepChange = (step: number): void => {
    setActiveStep(step);
  };

  return (
    <div css={styles.container}>
      {isLoading && <DashboardPromoSkeleton />}
      {!isLoading && content?.length > 0 && (
        <>
          <AutoPlaySwipeableViews
            axis={'x'}
            index={activeStep}
            onChangeIndex={handleStepChange}
            enableMouseEvents
            interval={8000}
          >
            {content?.map((item) => (
              <DashboardPromo key={item.id} promo={item} carouselItem />
            ))}
          </AutoPlaySwipeableViews>
          <section css={styles.stepper}>
            {content.map((_, i) => (
              <button
                key={i}
                onClick={(): void => handleStepChange(i)}
                css={styles.stepButton(activeStep === i)}
              ></button>
            ))}
          </section>
        </>
      )}
    </div>
  );
};

export default PromoCarousel;
